import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import clsx from 'clsx';
import { ChevronDownIcon, ChevronUpDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import type { ReactNode } from 'react';
import { TH, TH_SORTABLE, TH_PINNED_LEFT } from '../../vacationTable.styles';

export interface VacationDpSortableHeaderProps {
    id: string;
    children: ReactNode;
    onSort?: (event: unknown) => void;
    canSort: boolean;
    sorted: false | 'asc' | 'desc';
    headerAlign?: 'left' | 'right' | 'center';
    isSticky: boolean;
    align?: 'left' | 'right';
    isPinned?: boolean;
    pinnedLeft?: number;
}

function sortIndicator(sorted: false | 'asc' | 'desc') {
    if (sorted === 'asc') {
        return <ChevronUpIcon className="h-3.5 w-3.5 text-primary-600" aria-hidden="true" />;
    }
    if (sorted === 'desc') {
        return <ChevronDownIcon className="h-3.5 w-3.5 text-primary-600" aria-hidden="true" />;
    }
    return <ChevronUpDownIcon className="h-3.5 w-3.5 text-raro-gray-300" aria-hidden="true" />;
}

function headerAlignmentClass(align?: 'left' | 'right' | 'center'): string {
    if (align === 'center') return 'justify-center';
    if (align === 'right') return 'flex-row-reverse justify-end';
    return 'justify-start';
}

function alignmentClass(align?: 'left' | 'right'): string {
    return align === 'right' ? 'text-right' : 'text-left';
}

export function VacationDpSortableHeader({
    id,
    children,
    onSort,
    canSort,
    sorted,
    headerAlign = 'left',
    isSticky,
    align,
    isPinned,
    pinnedLeft,
}: VacationDpSortableHeaderProps) {
    const { attributes, listeners, isDragging, transform, setNodeRef } = useSortable({
        id,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition: 'all 150ms ease',
        zIndex: isDragging ? 1000 : isPinned ? 20 : 'auto',
        ...(isPinned ? { left: pinnedLeft } : {}),
    };

    return (
        <th
            ref={setNodeRef}
            style={style}
            scope="col"
            onClick={onSort}
            className={clsx(
                TH,
                alignmentClass(align),
                isSticky && 'sticky top-0 bg-white',
                isPinned && TH_PINNED_LEFT,
                canSort && TH_SORTABLE,
                isDragging && 'opacity-50'
            )}
            {...attributes}
            {...listeners}
        >
            <span
                className={clsx(
                    'flex w-full items-center gap-1 cursor-grab active:cursor-grabbing',
                    headerAlignmentClass(headerAlign)
                )}
            >
                {children}
                {canSort && sortIndicator(sorted)}
            </span>
        </th>
    );
}

export default VacationDpSortableHeader;
